import { useState, useRef, useCallback } from 'react';
import { useAppContext } from '../contexts/AppContext';

export const useRewriteFeature = (textareaRef) => {
  const { state, actions } = useAppContext();
  const { reportText, selectedText, showRewriteButton, buttonPosition } = state;
  const isRewritePopupOpen = state.modals.rewrite;

  const {
    setReportText,
    setSelectedText,
    setRewriteButton,
    setButtonPosition,
    setModalState
  } = actions;

  const setIsRewritePopupOpen = useCallback((isOpen) => {
    setModalState('rewrite', isOpen);
  }, [setModalState]);

  const [selectionStart, setSelectionStart] = useState(0);
  const [selectionEnd, setSelectionEnd] = useState(0);
  
  const selectionTimeoutRef = useRef(null);

  const handleTextSelection = useCallback(() => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value.substring(start, end);

    if (text.length > 0) {
      setSelectedText(text);
      setSelectionStart(start);
      setSelectionEnd(end);
      
      // Calculate button position
      const rect = textarea.getBoundingClientRect();
      const textBeforeSelection = textarea.value.substring(0, start);
      const lines = textBeforeSelection.split('\n');
      const currentLine = lines.length;
      const charInLine = lines[lines.length - 1].length;
      
      // Rough calculation for position
      const lineHeight = 20;
      const charWidth = 8;
      const x = rect.left + (charInLine * charWidth);
      const y = rect.top + (currentLine * lineHeight) - 40;
      
      setButtonPosition({ x, y });
      setRewriteButton(true);

      // Hide button after 3 seconds
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
      selectionTimeoutRef.current = setTimeout(() => {
        setRewriteButton(false);
      }, 3000);
    } else {
      setRewriteButton(false);
      setSelectedText('');
    }
  }, [textareaRef, setSelectedText, setButtonPosition, setRewriteButton]);

  const handleOpenRewritePopup = useCallback(() => {
    if (selectedText) {
      setIsRewritePopupOpen(true);
      setRewriteButton(false);
    }
  }, [selectedText, setIsRewritePopupOpen, setRewriteButton]);

  const handleApplyRewrite = useCallback(async (rewrittenText) => {
    if (!textareaRef.current) return;

    const textarea = textareaRef.current;
    const beforeText = reportText.substring(0, selectionStart);
    const afterText = reportText.substring(selectionEnd);
    const newText = beforeText + rewrittenText + afterText;
    
    setReportText(newText);
    setIsRewritePopupOpen(false);
    setSelectedText('');
    
    // Focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPosition = selectionStart + rewrittenText.length;
      textarea.setSelectionRange(newCursorPosition, newCursorPosition);
    }, 100);
  }, [reportText, selectionStart, selectionEnd, setReportText, setIsRewritePopupOpen, setSelectedText, textareaRef]);

  const getContextForRewrite = useCallback(() => {
    const contextLength = 100;
    const start = Math.max(0, selectionStart - contextLength);
    const end = Math.min(reportText.length, selectionEnd + contextLength);
    return reportText.substring(start, end);
  }, [reportText, selectionStart, selectionEnd]);

  const handleMouseDown = useCallback((e) => {
    const handleMouseMove = (e) => {
      // Mouse is moving, so it's likely a selection
    };

    const handleMouseUp = () => {
      // Check for text selection after a short delay
      setTimeout(handleTextSelection, 10);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleTextSelection]);

  const handleKeyDown = useCallback((e) => {
    // Handle Tab key for indentation
    if (e.key === 'Tab') {
      e.preventDefault();
      const textarea = e.target;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const value = textarea.value;
      const newValue = value.substring(0, start) + '    ' + value.substring(end);
      setReportText(newValue);
      
      // Set cursor position after tab
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + 4;
      }, 0);
    }
  }, [setReportText]);

  return {
    selectedText,
    selectionStart,
    selectionEnd,
    isRewritePopupOpen,
    setIsRewritePopupOpen,
    showRewriteButton,
    buttonPosition,
    handleTextSelection,
    handleOpenRewritePopup,  
    handleApplyRewrite,
    getContextForRewrite,
    handleMouseDown,
    handleKeyDown
  };
}; 